<script lang="ts">
export default {
  name: 'TextArea',
};
</script>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick, watch } from 'vue';
import { cn } from '@/lib/utils';

interface Props {
  class?: string;
  modelValue?: string;
  PermanentBorder?: boolean;
  autoResize?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  autoResize: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const textareaRef = ref<HTMLTextAreaElement>();

const textareaClass = computed(() =>
  cn(
    'tw:flex tw:min-h-[80px] tw:w-full tw:rounded-md tw:bg-background tw:px-3 tw:py-2 tw:text-sm tw:ring-offset-background tw:placeholder:text-muted-foreground tw:focus-visible:outline-none tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
    // Conditional border and ring styles based on PermanentBorder prop
    props.PermanentBorder 
      ? 'tw:border-2 tw:ring-2 tw:ring-offset-2' 
      : 'tw:border tw:border-input tw:focus-visible:ring-2 tw:focus-visible:ring-offset-2',
    props.class,
  ),
);

const adjustHeight = () => {
  if (!props.autoResize || !textareaRef.value) return;
  
  const textarea = textareaRef.value;
  
  // Reset height to auto to get the natural height
  textarea.style.height = 'auto';
  
  // Set height to scrollHeight to fit all content
  textarea.style.height = `${textarea.scrollHeight}px`;
  
  // Ensure no overflow
  textarea.style.overflow = 'hidden';
};

onMounted(() => {
  if (props.autoResize) {
    // Use a small timeout to ensure DOM is fully rendered
    setTimeout(() => {
      adjustHeight();
    }, 0);
  }
});

watch(() => props.modelValue, () => {
  if (props.autoResize) {
    nextTick(() => {
      adjustHeight();
    });
  }
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  emit('update:modelValue', target.value);
  
  if (props.autoResize) {
    // Use requestAnimationFrame for smoother resizing
    requestAnimationFrame(() => {
      adjustHeight();
    });
  }
};
</script>

<template>
  <textarea
    ref="textareaRef"
    :class="textareaClass"
    :value="modelValue"
    @input="handleInput"
    v-bind="$attrs" />
</template>
